import { useEffect, useLayoutEffect, useMemo, useState } from 'preact/hooks';
import type { CoasterRuntime, RuntimeSnapshot } from '../app/runtime-types.ts';

interface CoachmarkCopy {
  readonly target: string;
  readonly eyebrow: string;
  readonly title: string;
  readonly body: string;
  readonly step: string;
  readonly action?: string;
}

const CREATOR_COPY = {
  station: {
    target: 'station-drop-zone',
    eyebrow: 'First Build',
    title: 'Place your station',
    body: 'Click or tap the marked spot near the center of the build area.',
    step: '1 of 18',
  },
  first_piece: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Place one track piece',
    body: 'Straight track is ready. Place it to begin your route.',
    step: '2 of 18',
  },
  uphill_slope: {
    target: 'uphill-slope',
    eyebrow: 'First Build',
    title: 'Start an uphill slope',
    body: 'Choose the 25° uphill slope for the next piece.',
    step: '3 of 18',
  },
  powered_choice: {
    target: 'track-type',
    eyebrow: 'First Build',
    title: 'Add some power',
    body: 'Lift pulls the train uphill. Launch boosts it forward. Choose either one.',
    step: '3 of 18',
  },
  powered_transition: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Begin the powered hill',
    body: 'Place the uphill Lift or Launch piece.',
    step: '3 of 18',
  },
  powered_climb: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Build the hill',
    body: 'Place four more uphill pieces in a row. Keep the same power type selected.',
    step: '4 of 18',
  },
  level_off_choice: {
    target: 'level-slope',
    eyebrow: 'First Build',
    title: 'Level off',
    body: 'Choose Level while keeping Lift or Launch selected.',
    step: '5 of 18',
  },
  level_off_place: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Finish the climb',
    body: 'Place the level-off piece at the top of the hill.',
    step: '5 of 18',
  },
  turn_back_choice: {
    target: 'left-large-turn',
    eyebrow: 'First Build',
    title: 'Turn back toward the station',
    body: 'Choose the large left turn.',
    step: '6 of 18',
  },
  turn_back_place: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Make a U-turn',
    body: 'Place two large left turns in a row.',
    step: '6 of 18',
  },
  downhill_choice: {
    target: 'downhill-slope',
    eyebrow: 'First Build',
    title: 'Head downhill',
    body: 'Choose the 25° downward slope.',
    step: '7 of 18',
  },
  downhill_transition: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Start the drop',
    body: 'Place the first downward transition.',
    step: '7 of 18',
  },
  downhill_run: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Keep heading down',
    body: 'Place one straight downhill piece.',
    step: '7 of 18',
  },
  endpoint: {
    target: 'current-endpoint',
    eyebrow: 'First Build',
    title: 'Know where you are',
    body: 'Watch your height change here as you build back toward station level.',
    step: '7 of 18',
    action: 'Got it',
  },
  downhill_run_finish: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Return to station height',
    body: 'Place three more downhill pieces while watching your height.',
    step: '7 of 18',
  },
  downhill_level_choice: {
    target: 'level-slope',
    eyebrow: 'First Build',
    title: 'Level the track',
    body: 'Choose Level now that you are back at station height.',
    step: '8 of 18',
  },
  downhill_level_place: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Finish the drop',
    body: 'Place the level-off piece.',
    step: '8 of 18',
  },
  special_tab: {
    target: 'special-track-tab',
    eyebrow: 'First Build',
    title: 'Explore Special Track',
    body: 'Open Special Track to find coaster elements beyond the regular controls.',
    step: '9 of 18',
  },
  barrel_roll_choice: {
    target: 'barrel-roll-left',
    eyebrow: 'First Build',
    title: 'Choose a Barrel Roll',
    body: 'This left Barrel Roll works well at the speed of this tutorial coaster.',
    step: '9 of 18',
  },
  barrel_roll_place: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Add the Barrel Roll',
    body: 'Place the selected special element.',
    step: '9 of 18',
  },
  regular_tab: {
    target: 'regular-track-tab',
    eyebrow: 'First Build',
    title: 'Return to Regular Track',
    body: 'Go back to the regular building controls.',
    step: '10 of 18',
  },
  route_guide: {
    target: 'route-guide-help',
    eyebrow: 'First Build',
    title: 'Help getting home',
    body: 'Route Guide can suggest your next pieces. Autocomplete can finish the route for you after this tutorial. Both are optional.',
    step: '10 of 18',
    action: 'Got it',
  },
  photo_choice: {
    target: 'track-type-photo',
    eyebrow: 'First Build',
    title: 'Add an Action Photo',
    body: 'Choose Photo for the next straight piece.',
    step: '11 of 18',
  },
  photo_place: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Place the Photo track',
    body: 'This takes an Action Photo as the train passes.',
    step: '11 of 18',
  },
  bank_home_choice: {
    target: 'bank-left',
    eyebrow: 'First Build',
    title: 'Bank into the turn',
    body: 'Choose Left banking. Banked track leans the train into a turn.',
    step: '12 of 18',
  },
  bank_home_place: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Start the bank',
    body: 'Place the transition from level track to a left bank.',
    step: '12 of 18',
  },
  turn_home_choice: {
    target: 'left-large-turn',
    eyebrow: 'First Build',
    title: 'Face the station',
    body: 'Choose the large left turn again.',
    step: '12 of 18',
  },
  turn_home_place: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Turn toward home',
    body: 'Place two large left turns in a row.',
    step: '12 of 18',
  },
  bank_straight_choice: {
    target: 'direction-straight',
    eyebrow: 'First Build',
    title: 'Exit the turn',
    body: 'Choose Straight before leveling the track.',
    step: '13 of 18',
  },
  bank_level_choice: {
    target: 'bank-level',
    eyebrow: 'First Build',
    title: 'Level out',
    body: 'Choose Level banking before the brake run.',
    step: '13 of 18',
  },
  bank_level_place: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Finish the banked turn',
    body: 'Place the transition back to level track.',
    step: '13 of 18',
  },
  undo_demo: {
    target: 'undo',
    eyebrow: 'First Build',
    title: 'Try Undo',
    body: 'Undo the piece you just placed. You can use this whenever a build choice does not work out.',
    step: '14 of 18',
  },
  redo_demo: {
    target: 'redo',
    eyebrow: 'First Build',
    title: 'Now Redo',
    body: 'Redo restores that piece so you can keep building from the same spot.',
    step: '14 of 18',
  },
  brake_choice: {
    target: 'track-type-brake',
    eyebrow: 'First Build',
    title: 'Slow down for the station',
    body: 'Choose Brakes for the straight approach.',
    step: '15 of 18',
  },
  brake_run: {
    target: 'place-piece',
    eyebrow: 'First Build',
    title: 'Build into the station',
    body: 'Place brake pieces until the track connects to the station.',
    step: '15 of 18',
  },
  ride_check: {
    target: 'editor-tools',
    eyebrow: 'First Build',
    title: 'Optional - Explore your coaster tools',
    body: 'This menu also has Scenery, Style themes, and Ride Check. Explore any of them, or continue when you are ready to test.',
    step: '16 of 18',
    action: 'Ready to test',
  },
  test_ride: {
    target: 'test-ride',
    eyebrow: 'First Build',
    title: 'Take your first Test Ride',
    body: 'Complete the ride to prove this coaster works.',
    step: '17 of 18',
  },
  unlocks: {
    target: 'post-test-actions',
    eyebrow: 'First Build',
    title: 'Your first coaster works!',
    body: 'You can now Record your coaster to make a shareable video, or submit it to be Published on Buncha Games.',
    step: '18 of 18',
    action: 'Finish guide',
  },
} as const satisfies Record<string, CoachmarkCopy>;

const FINISH_COPY = {
  endpoint: {
    target: 'current-endpoint',
    eyebrow: 'Finish This Coaster',
    title: 'Read the endpoint',
    body: 'This route is close to home. Check its distance and height before choosing pieces.',
    step: '1 of 6',
    action: 'Next',
  },
  route_guide: {
    target: 'route-guide',
    eyebrow: 'Finish This Coaster',
    title: 'Turn on Route Guide',
    body: 'Route Guide marks official pieces that lead back to the station.',
    step: '2 of 6',
  },
  close_track: {
    target: 'basic-composer',
    eyebrow: 'Finish This Coaster',
    title: 'Close the circuit',
    body: 'Place the highlighted recommendations until the track connects to the station.',
    step: '3 of 6',
  },
  ride_check: {
    target: 'ride-check',
    eyebrow: 'Finish This Coaster',
    title: 'Open Ride Check',
    body: 'Review the completed route before testing it.',
    step: '4 of 6',
    action: 'Review approach',
  },
  brake: {
    target: 'closed-track-type',
    eyebrow: 'Finish This Coaster',
    title: 'Smooth the final approach',
    body: 'Change the highlighted straight approach to Brakes.',
    step: '5 of 6',
  },
  test_ride: {
    target: 'test-ride',
    eyebrow: 'Finish This Coaster',
    title: 'Test your finished ride',
    body: 'Complete a Test Ride to finish the lesson.',
    step: '6 of 6',
  },
} as const satisfies Record<string, CoachmarkCopy>;

function visibleTarget(name: string): HTMLElement | null {
  return (
    [...document.querySelectorAll<HTMLElement>(`[data-onboarding-target="${name}"]`)].find(
      (element) => {
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      },
    ) ?? null
  );
}

export function OnboardingCoachmark({
  state,
  runtime,
}: {
  readonly state: RuntimeSnapshot;
  readonly runtime: CoasterRuntime | null;
}) {
  const copy = useMemo<CoachmarkCopy | null>(() => {
    if (state.onboarding.creator.active && state.onboarding.creator.step)
      return CREATOR_COPY[state.onboarding.creator.step];
    if (state.onboarding.finishLesson.active && state.onboarding.finishLesson.step)
      return FINISH_COPY[state.onboarding.finishLesson.step];
    return null;
  }, [state.onboarding]);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!copy) return;
    let frame = 0;
    const update = () => {
      const target = visibleTarget(copy.target);
      document
        .querySelectorAll('[data-onboarding-active]')
        .forEach((element) => element.removeAttribute('data-onboarding-active'));
      if (!target) {
        setPosition(null);
        return;
      }
      target.setAttribute('data-onboarding-active', 'true');
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      const rect = target.getBoundingClientRect();
      const width = Math.min(330, window.innerWidth - 24);
      const gap = 14;
      const left =
        rect.left - gap >= width
          ? rect.left - width - gap
          : rect.right + gap + width <= window.innerWidth
            ? rect.right + gap
            : Math.max(12, Math.min(window.innerWidth - width - 12, rect.left));
      const top = Math.max(12, Math.min(window.innerHeight - 210, rect.top));
      setPosition({ left, top });
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    schedule();
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    const observer = new ResizeObserver(schedule);
    observer.observe(document.documentElement);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      document
        .querySelectorAll('[data-onboarding-active]')
        .forEach((element) => element.removeAttribute('data-onboarding-active'));
    };
  }, [copy]);

  useEffect(() => {
    if (!copy) return;
    document.querySelector<HTMLElement>('.onboarding-coachmark')?.focus();
  }, [copy?.target]);

  if (!copy) return null;
  const creator = state.onboarding.creator.active;
  return (
    <aside
      class="onboarding-coachmark"
      role="dialog"
      aria-modal="false"
      aria-label={copy.title}
      tabIndex={-1}
      style={position ? { left: `${position.left}px`, top: `${position.top}px` } : undefined}
    >
      <span class="eyebrow">
        {copy.eyebrow} · {copy.step}
      </span>
      <strong>{copy.title}</strong>
      <p>{copy.body}</p>
      <div>
        {copy.action && (
          <button
            class="primary-action"
            type="button"
            onClick={() =>
              creator ? runtime?.advanceCreatorGuide() : runtime?.advanceFinishLesson()
            }
          >
            {copy.action}
          </button>
        )}
        {creator && state.onboarding.creator.step !== 'unlocks' && (
          <button type="button" onClick={() => runtime?.skipCreatorGuide()}>
            Skip guide
          </button>
        )}
      </div>
    </aside>
  );
}
